import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { authenticate } from '@actions/auth';
import { followGoal } from '@api/fetchers/goal/updaters';
import { submitCity } from '@api/fetchers/auth';
import useSelectedGoal from '@api/hooks/topology/useSelectedGoal';
import { segment } from '@actions/analytics';
import { useDispatch } from 'react-redux';
import { useCommonData, useIsMobile } from '@stores/AppCommonData';
import { getMarkLoginProcessCompleted } from '@stores/selectors/common';
import useMobileLoginCountries from '@api/hooks/useMobileLoginCountries';
import {
  getInitialAccountType,
  getIsNewLoginModal,
  getIsRegistered,
  getOTPDataCleaner,
  getSetInitialAccountType,
  getSetIsNewLoginModal,
  getSetLocalPhoneNumber
} from '@stores/selectors/login';
import useLoginStore from '@stores/login/loginStore';
import useRewardEventStore from '@stores/rewards';
import Constants, {
  initialFieldsData,
  defaultCountry,
  LOGIN_SELECT_AGE,
  LOGIN_SOURCE_TTU
} from './constants';
import LoginComponentLayout from './Layout';
import { LoginLocalInfoContextProvider } from './Context/LoginLocalInfo';
import setCookiesPostLogin from './utils/setCookies';
// Login Functionality Hooks
import useLoginInitializer from './utils/hooks/useLoginInitializer';
import useOutsideIndiaLoginType from './utils/hooks/useOutsideIndiaLoginType';
import useLoginStepSetter from './utils/hooks/useLoginStepSetter';
import useChangeCountry from './utils/hooks/useChangeCountry';
import useSignUpStepsLogger from './utils/hooks/useSignUpStepsLogger';
import useLogOTPRequestedEvent from './utils/hooks/useLogOTPRequestedEvent';
import useSubmitNumber from './utils/hooks/useSubmitNumber';
import useIsOutsideIndiaFlow from './utils/hooks/useIsOutsideIndiaFlow';
import useSubmitEmail from './utils/hooks/useSubmitEmail';
import useResendOTP from './utils/hooks/useResendOTP';
import usePostLoginActions from './utils/hooks/usePostLoginActions';
import useSubmitLogin from './utils/hooks/useSubmitLogin';
import LoginUI from './LoginUI';
import useLocalPhoneNumber from './utils/hooks/useLocalPhoneNumber';
import useEnableLoginModal from './utils/hooks/useEnableLoginModal';

// Login steps and processes - https://www.notion.so/unacademy/Modified-Login-And-Sign-Up-Flow-658915a3f31f474a8877514e7cd64c0e

const LoginFlow = ({
  source = 'drawer',
  showOtpField = false,
  phoneNumber = '',
  hideHelp = false,
  sourceType = '',
  updateForm = null,
  handleSourceLogin = null,
  userInfo = {},
  loginCallback = null,
  ttuUserName = '',
  presetRedirectURL = '',
  /* REFERRAL_PROMOTIONS — begin */
  countrySelected = null,
  campaignTheme = false,
  /* REFERRAL_PROMOTIONS — end */
  primeTheme = false
}) => {
  const dispatch = useDispatch();
  const router = useRouter();
  useEnableLoginModal({ source });
  const { localSelectedCountry, localPhoneNumber } = useLocalPhoneNumber(); // when there is pre entered phone number
  const setLocalPhoneNumber = useLoginStore(getSetLocalPhoneNumber);
  const initialAccountType = useLoginStore(getInitialAccountType);
  const setInitialAccountType = useLoginStore(getSetInitialAccountType);
  const IsNewLoginModal = useLoginStore(getIsNewLoginModal);
  const [step, setStep] = useState(Constants.ENTER_NUMBER_STEP);
  const [loginType, setLoginType] = useState(Constants.LOGIN_TYPE_NUMBER);
  const [mobileNumber, setMobileNumber] = useState(
    localPhoneNumber || phoneNumber
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [fieldsData, setFieldsData] = useState(initialFieldsData);
  const [numberStepErrorMsg, setNumberStepErrorMsg] = useState('');
  const [otpStepError, setOtpStepError] = useState(null);

  let countries = useMobileLoginCountries();
  if (!countries.length) countries = [defaultCountry];

  const isRegistered = useLoginStore(getIsRegistered);
  const resetOtpResponse = useLoginStore(getOTPDataCleaner);

  const isOutsideIndiaFlow = useIsOutsideIndiaFlow();

  const isMobile = useIsMobile();
  const { pathname } = router;
  const isCheckout = /subscribe\/.*/g.test(pathname);
  const getAccountType = () => {
    let type = initialAccountType || Constants.ACCOUNT_TYPE_LOGIN;
    if (isCheckout && isMobile) type = Constants.ACCOUNT_TYPE_SIGNUP;
    return type;
  };
  const [accountType, setAccountType] = useState(() => getAccountType());
  const [country, setCountry] = useState(
    localSelectedCountry ||
      countrySelected || // when there is pre selected country
      (countries.length > 0
        ? countries.find((c) => c.code === 'IN')
        : defaultCountry)
  );

  const [selectedAgeGroup, setSelectedAgeGroup] = useState(null);

  const clearFormData = () => {
    setFieldsData(initialFieldsData);
    setOtp('');
  };

  const resetErrorMsgs = () => {
    setNumberStepErrorMsg('');
    setOtpStepError(null);
  };

  const switchAccountType = () => {
    resetErrorMsgs();
    if (accountType === Constants.ACCOUNT_TYPE_LOGIN) {
      setAccountType(Constants.ACCOUNT_TYPE_SIGNUP);
      setLoginType(
        isOutsideIndiaFlow
          ? Constants.LOGIN_TYPE_EMAIL
          : Constants.LOGIN_TYPE_NUMBER
      );
      setEmail('');
    } else {
      setAccountType(Constants.ACCOUNT_TYPE_LOGIN);
    }
  };

  const switchLoginType = () => {
    resetErrorMsgs();
    resetOtpResponse();
    if (loginType === Constants.LOGIN_TYPE_NUMBER) {
      setLoginType(Constants.LOGIN_TYPE_EMAIL);
      setMobileNumber('');
    } else {
      setLoginType(Constants.LOGIN_TYPE_NUMBER);
      setEmail('');
    }
  };

  let loginLocalInfoContextValue = {
    loginType,
    step,
    accountType,
    email,
    otp,
    password,
    source,
    sourceType,
    country,
    setCountry,
    countries,
    mobileNumber,
    fieldsData,
    setStep,
    numberStepErrorMsg,
    otpStepError,
    ttuUserName,
    isTTU: source === LOGIN_SOURCE_TTU,
    selectedAgeGroup,
    isMinor: selectedAgeGroup?.isMinor || false,
    hideHelp,
    showOtpField,
    isOutsideIndiaSignup:
      accountType === Constants.ACCOUNT_TYPE_SIGNUP && isOutsideIndiaFlow,
    setAccountType,
    changeAccountType: switchAccountType,
    changeLoginType: switchLoginType,
    setEmail,
    setOtp,
    setPassword,
    setMobileNumber,
    setFieldsData,
    setNumberStepErrorMsg,
    setOtpStepError,
    IsNewLoginModal,
    primeTheme,
    /* REFERRAL_PROMOTIONS */
    campaignTheme
    /* REFERRAL_PROMOTIONS */
  };

  useLoginInitializer(loginLocalInfoContextValue);
  useLoginStepSetter(loginLocalInfoContextValue);
  useOutsideIndiaLoginType(loginLocalInfoContextValue);
  const setIsNewLoginModal = useLoginStore(getSetIsNewLoginModal);
  const changeCountry = useChangeCountry(loginLocalInfoContextValue);
  const logOTPRequestedEvent = useLogOTPRequestedEvent({
    loginType,
    presetRedirectURL,
    source
  });
  const submitNumber = useSubmitNumber({
    ...loginLocalInfoContextValue,
    logOTPRequestedEvent
  });
  const logSignUpSteps = useSignUpStepsLogger(source);
  const submitEmail = useSubmitEmail(loginLocalInfoContextValue);
  const resendOtp = useResendOTP({
    ...loginLocalInfoContextValue,
    logOTPRequestedEvent
  });

  const goBackToFirstStep = (event) => {
    resetOtpResponse();
    setStep(Constants.ENTER_NUMBER_STEP);
    clearFormData();
    resetErrorMsgs();
    if (updateForm) updateForm();
    event.preventDefault();
  };
  const { selectedGoalInfo } = useSelectedGoal();
  const markLoginProcessCompleted = useCommonData(getMarkLoginProcessCompleted);

  const postLoginActions = usePostLoginActions({
    country,
    mobileNumber,
    presetRedirectURL,
    loginType,
    loginCallback
  });

  const handleAgeGroupConfirmation = (ageGroup) => {
    setSelectedAgeGroup(ageGroup);
    segment.track(LOGIN_SELECT_AGE);
    setStep(Constants.ENTER_OTP_STEP);
  };

  const { selectedGoal } = useRewardEventStore();
  const isRewardEvent = /event\/rewards\/.*/g.test(pathname);
  const afterLogin = async (
    response = null,
    triggeredFromDetailsPage = false
  ) => {
    dispatch(
      authenticate({
        accessToken: response?.access_token,
        refreshToken: response?.refresh_token
      })
    );
    setCookiesPostLogin(response, source);
    markLoginProcessCompleted();

    if (sourceType === 'marketing' && handleSourceLogin)
      return handleSourceLogin(response);

    if (!isRegistered && selectedGoalInfo?.uid)
      await followGoal(selectedGoalInfo.uid, true);

    if (isRewardEvent && !isRegistered && selectedGoal?.uid) {
      await followGoal(selectedGoal.uid, true);
    }

    if (isRewardEvent && !isRegistered && fieldsData.city?.value) {
      await submitCity(fieldsData.city.value);
    }

    if (isRewardEvent && isRegistered) {
      window.location.reload();
    }

    postLoginActions(triggeredFromDetailsPage);
  };

  const submitLogin = useSubmitLogin({
    ...loginLocalInfoContextValue,
    userInfo,
    afterLogin,
    presetRedirectURL,
    handleSourceLogin
  });

  loginLocalInfoContextValue = {
    ...loginLocalInfoContextValue,
    changeCountry,
    handleAgeGroupConfirmation,
    logOTPRequestedEvent,
    resendOtp,
    submitNumber,
    submitEmail,
    submitLogin,
    logSignUpSteps,
    goBackToFirstStep
  };

  useEffect(() => {
    return () => {
      setInitialAccountType(null);
      setLocalPhoneNumber(null, null);
    };
  }, [setInitialAccountType, setLocalPhoneNumber, setIsNewLoginModal]);

  return (
    <LoginLocalInfoContextProvider value={loginLocalInfoContextValue}>
      <LoginComponentLayout>
        <LoginUI />
      </LoginComponentLayout>
    </LoginLocalInfoContextProvider>
  );
};

export default LoginFlow;
